/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import * as Joi from 'joi-browser';

import withStyles from '@material-ui/core/styles/withStyles';
import Button from '@material-ui/core/Button';

import Form from '../../Form';
import { form as FormStyle } from '../../../style/form';
import ErrorInline from '../../ErrorMessages/ErrorInline';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import configENV from '../../../config';

const { PERSIST_KEY } = configENV;
const styles = ({ typography, palette }) => FormStyle(typography, palette);

class ChangePasswordForm extends Form {
    state = {
        data: {
            currentPassword: '',
            newPassword: '',
        },
        showPassword: {
            currentPassword: false,
            newPassword: false,
        },
        isSubmited: false,
        errors: {},
    };

    schema = {
        currentPassword: Joi.string()
            .trim()
            .regex(
                /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{6,}$/,
                'password must be at least 6 characters long with 1 number and 1 letter',
            )
            .min(6)
            .max(50)
            .required()
            .label('Current Password'),
        newPassword: Joi.string()
            .trim()
            .invalid(Joi.ref('currentPassword'))
            .regex(
                /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{6,}$/,
                'password must be at least 6 characters long with 1 number and 1 letter',
            )
            .min(6)
            .max(50)
            .required()
            .label('New Password'),
    };

    componentDidMount = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (props) => {
        const { isSubmited } = this.state;
        const { isReadyState } = props;
        // Reset password fields
        if (isSubmited && isReadyState) {
            const data = {
                currentPassword: '',
                newPassword: '',
            };
            this.setState({ isSubmited: false, data });
        }
    };

    doSubmit = (submitData) => {
        const { data } = this.state;
        this.setState({ isSubmited: true }, () => {
            submitData.handleUpdatePassword(data);
        });
    };

    handleNewPasswordChange = (dataChanged) => {
        // eslint-disable-next-line react/jsx-props-no-spreading
        const data = { ...dataChanged };
        if (data.currentPassword === data.newPassword) {
            const errors = {
                newPassword:
                    'New password cannot be the same as current password.',
            };
            this.setState({ errors });
        }
    };

    handleContinueShopping = () => {
        const { handleCloseModal, handleUpdatePassword } = this.props;
        handleCloseModal();
        handleUpdatePassword({ status: '' }); // reset form status so user can change PW again in same session
        this.props.history.push('/');
    };

    updatePasswordForm = (props) => {
        const { isReadyState, classes, handleUpdatePassword } = props;
        const submitButtonLabel = isReadyState
            ? 'Update Password'
            : 'Updating...';
        const submitData = {
            handleUpdatePassword,
        };
        return (
            <>
                <form
                    onSubmit={(event) => this.handleSubmit(event, submitData)}
                    className={`${classes.form} row margin-top-2x`}
                    disabled={!isReadyState}
                >
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <ErrorInline {...props} />
                    <div>
                        {this.renderInput({
                            name: 'currentPassword',
                            label: 'Current Password',
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                        })}
                    </div>
                    <div>
                        {this.renderInput({
                            name: 'newPassword',
                            label: 'New Password',
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                            onChangeHandler: this.handleNewPasswordChange,
                        })}
                    </div>
                    <div className="margin-top-2x opacity1">
                        {this.renderButton({
                            label: submitButtonLabel,
                            className: 'buttonHD',
                            onClick: null,
                            type: 'submit',
                            isValidated: true,
                            isReadyState,
                        })}
                    </div>
                </form>
            </>
        );
    };

    continueShopping = (successMessage) => (
        <>
            <div className="margin-top-2x bold">{successMessage}</div>
            <div className="margin-top-2x row">
                <Button
                    onClick={() => this.handleContinueShopping()}
                    variant="contained"
                    color="primary"
                    className="remove-recipient-btn buttonHD"
                >
                    Continue Shopping
                </Button>
            </div>
        </>
    );

    render() {
        const { status, successMessage } = this.props;
        return (
            <>
                {status !== 'success' ? (
                    <p>
                        To change your password, you must first confirm your
                        current password. Once correct, then enter your new
                        password.
                    </p>
                ) : (
                    ''
                )}
                {status === 'success'
                    ? this.continueShopping(successMessage)
                    : this.updatePasswordForm(this.props)}
            </>
        );
    }
}

ChangePasswordForm.defaultProps = {
    status: '',
    successMessage: '',
    error: {},
    handleUpdatePassword: () => { },
    handleCloseModal: () => { },
};

ChangePasswordForm.propTypes = {
    classes: PropTypes.object.isRequired,
    error: PropTypes.object,
    isReadyState: PropTypes.bool.isRequired,
    status: PropTypes.string,
    successMessage: PropTypes.string,
    handleUpdatePassword: PropTypes.func,
    handleCloseModal: PropTypes.func,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isReadyState: newState.isReadyState,
        status: newState.status,
        successMessage: newState.successMessage,
        error: newState.error,
        toggleState: newState.toggleState,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleUpdatePassword: (data) => dispatch(EventDispatcher.handleUpdatePassword(data)),
    handleCloseModal: () => dispatch(EventDispatcher.handleCloseModal()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles)(ChangePasswordForm)));
